import { Button, Container } from "@material-ui/core";
import { makeStyles } from "@material-ui/core/styles";
import { ConfirmButtonTransitionState } from "@saleor/components/ConfirmButton";
import Form from "@saleor/components/Form";
import Grid from "@saleor/components/Grid";
import PageBackNavigation from "@saleor/components/PageBackNavigation";
import PageBreadcrumbNavigation from "@saleor/components/PageBreadcrumbNavigation/PageBreadcrumbNavigation";
import PageHeader from "@saleor/components/PageHeader";
import SaveButtonBar from "@saleor/components/SaveButtonBar";
import { BrandErrorFragment } from "@saleor/fragments/types/BrandErrorFragment";
import { SubmitPromise } from "@saleor/hooks/useForm";
import useNotifier from "@saleor/hooks/useNotifier";
import useStateFromProps from "@saleor/hooks/useStateFromProps";
import { stopPropagation } from "@saleor/misc";
import { SearchBrandGroup_search_edges_node } from "@saleor/searches/types/SearchBrandGroup";
import { SearchClusters_search_edges_node } from "@saleor/searches/types/SearchClusters";
import { FetchMoreProps } from "@saleor/types";
import { ShopifyTagEnum } from "@saleor/types/globalTypes";
import createSingleAutocompleteSelectHandler from "@saleor/utils/handlers/singleAutocompleteSelectChangeHandler";
import { hasNoError, useValidation } from "@saleor/utils/validators/seller";
import React, { useState } from "react";
import { FormattedMessage, useIntl } from "react-intl";

import { maybe } from "../../../misc";
import { SellerDetails_brandPrivate } from "../../types/SellerDetails";
import SellerInfo from "../SellerInfo";
const useStyles = makeStyles(
  theme => ({
    buttonlabel: {
      "& span": {
        color: "#002972"
      }
    },
    redButton: {
      "&:hover": {
        backgroundColor: theme.palette.error.dark
      },
      backgroundColor: theme.palette.error.main,
      color: theme.palette.error.contrastText,
      marginLeft: theme.spacing(1)
    }
  }),
  { name: "SellerDetails" }
);

export interface SellerDetailsPageFormData {
  accountManager: any;
  bankName: string;
  bankAccountName: string;
  bankAccountNo: string;
  cluster: string;
  code: string;
  completeAddress: string;
  customerCode: string;
  financeName: string;
  financeEmail: string;
  financeMobileNo: string;
  id: string;
  isLargeTaxpayer: boolean;
  isPayoutEligible: boolean;
  legalName: string;
  logoAlt: string;
  name: string;
  shopifyApiShop: string;
  shopifyApiKey: string;
  shopifyApiSecret: string;
  signatoryName: string;
  signatoryEmail: string;
  signatoryDesignation: string;
  signatoryMobileNo: string;
  storeType: string;
  status: string;
  tinNo: string;
  vendorCode: string;
  vertical: string;
  shopifyNonCodTags: string[];
  shopifyExclusionTags: string[];
  swornDeclarationsFlag: boolean;
}

export interface SellerFormValidation {
  name: string;
  legalName: string;
  storeType: string;
  tinNo: string;
  signatoryName: string;
  signatoryEmail: string;
  signatoryDesignation: string;
  signatoryMobileNo: string;
  bankName: string;
  bankAccountName: string;
  bankAccountNo: string;
  financeName: string;
  financeEmail: string;
  financeMobileNo: string;
  vertical: string;
}
interface SellerDetailsProp {
  seller: SellerDetails_brandPrivate;
  errors: BrandErrorFragment[];
  updateStatusError?: {
    customerCode?: string;
    vendorCode?: string;
    vertical?: string;
  };
  disabled?: boolean;
  fetchMoreBrands?: FetchMoreProps;
  fetchBrands: (query: string) => void;
  onBack: () => void;
  onSubmit: (data: SellerDetailsPageFormData) => SubmitPromise;
  saveButtonBarState: ConfirmButtonTransitionState;
  onAssign: (assign: string) => void;
  onView: () => void;
  onImageDelete: () => void;
  onImageUpload: (file: File) => void;
  sellerList: SearchBrandGroup_search_edges_node[];
  clusterList: SearchClusters_search_edges_node[];
  onGenerateDeeplink: (id: string) => void;
  onFileSizeError: () => void;
  fetchClusters: (query: string) => void;
  fetchMoreClusters?: FetchMoreProps;
  handleShopifySync?: () => void;
  isShopifySyncLoading?: boolean;
  handleVerifyShopifyConnection: (
    shopifyApiSecret: string,
    shopifyApiShop: string
  ) => void;
  isShopifyVerifyCredentialsLoading?: boolean;
  hasShopifyPermission?: boolean;
  handleShopifyTags: (
    shopifyExclusionTags: string,
    shopifyNonCodTags: string
  ) => void;
  onTagAdd: (tag: string, type: ShopifyTagEnum) => void;
  onTagRemove: (tag: string, type: ShopifyTagEnum) => void;
}

export const SellerDetails: React.FC<SellerDetailsProp> = props => {
  const {
    seller,
    disabled,
    onBack,
    onSubmit,
    errors,
    updateStatusError,
    saveButtonBarState,
    fetchBrands,
    fetchMoreBrands,
    onAssign,
    onView,
    onImageDelete,
    onImageUpload,
    onGenerateDeeplink,
    onFileSizeError,
    sellerList,
    clusterList,
    fetchClusters,
    fetchMoreClusters,
    handleShopifySync,
    isShopifySyncLoading,
    handleVerifyShopifyConnection,
    isShopifyVerifyCredentialsLoading,
    hasShopifyPermission,
    onTagAdd,
    onTagRemove
  } = props;
  const intl = useIntl();
  const notify = useNotifier();
  // console.log(seller);
  const initialForm: SellerDetailsPageFormData = {
    accountManager: seller?.accountManager || null,
    bankAccountName: seller?.bankAccountName || "",
    bankAccountNo: seller?.bankAccountNo || "",
    bankName: seller?.bankName || "",
    cluster: seller?.cluster?.id || "",
    code: seller?.code || "",
    completeAddress: seller?.completeAddress || "",
    customerCode: seller?.customerCode || "",
    financeEmail: seller?.financeEmail || "",
    financeMobileNo: seller?.financeMobileNo || "",
    financeName: seller?.financeName || "",
    id: seller?.id || "",
    isLargeTaxpayer: seller?.isLargeTaxpayer || null,
    isPayoutEligible: seller?.isPayoutEligible || null,
    legalName: seller?.legalName || "",
    logoAlt: seller?.logo?.alt || "",
    name: seller?.name || "",
    shopifyApiKey: seller?.shopifyApiKey || "",
    shopifyApiSecret: seller?.shopifyApiSecret || "",
    shopifyApiShop: seller?.shopifyApiShop || "",
    shopifyExclusionTags: seller?.shopifyExclusionTags
      ? seller?.shopifyExclusionTags.map(item => item.tag)
      : [],
    shopifyNonCodTags: seller?.shopifyNonCodTags
      ? seller?.shopifyNonCodTags.map(item => item.tag)
      : [],
    signatoryDesignation: seller?.signatoryDesignation || "",
    signatoryEmail: seller?.signatoryEmail || "",
    signatoryMobileNo: seller?.signatoryMobileNo || "",
    signatoryName: seller?.signatoryName || "",
    status: seller?.status || "",
    storeType: seller?.storeType || "",
    swornDeclarationsFlag: seller?.swornDeclarationsFlag,
    tinNo: seller?.tinNo || "",
    vendorCode: seller?.vendorCode || "",
    vertical: seller?.vertical || ""
  };

  const classes = useStyles(props);
  const sellerStatus = seller?.status;
  const [sellerDisplayName, setSellerDisplayName] = useStateFromProps("");
  const [clusterDisplayName, setClusterDisplayName] = useStateFromProps(
    maybe(() => seller?.cluster?.name, "")
  );

  const sellerChoices = maybe(
    () =>
      sellerList.map(brandGroup => ({
        classification: brandGroup?.classification,
        label: brandGroup?.name,
        value: brandGroup?.name,
        vertical: brandGroup?.vertical
      })),
    []
  );

  const clusterChoices = maybe(
    () =>
      clusterList.map(cluster => ({
        label: cluster?.name,
        value: cluster?.id
      })),
    []
  );

  const initalFormValidation: SellerFormValidation = {
    bankAccountName: null,
    bankAccountNo: null,
    bankName: null,
    financeEmail: null,
    financeMobileNo: null,
    financeName: null,
    legalName: null,
    name: null,
    signatoryDesignation: null,
    signatoryEmail: null,
    signatoryMobileNo: null,
    signatoryName: null,
    storeType: null,
    tinNo: null,
    vertical: null
  };
  const [clientError, setErrorValdiation] = useState(initalFormValidation);

  const hanldeSubmit = event => {
    const validate = useValidation(event, seller?.status);
    if (hasNoError(validate)) {
      setErrorValdiation(validate);
      onSubmit(event);
    } else {
      if (validate?.internalDetails) {
        notify({
          status: "error",
          text: intl.formatMessage({
            defaultMessage:
              "Please complete the internal details / vertical type of the seller"
          })
        });
      }

      setErrorValdiation(validate);
    }
  };
  return (
    <>
      <Form
        initial={initialForm}
        onSubmit={hanldeSubmit}
        confirmLeave
        noAutocomplete={true}
      >
        {({ data, change, submit, hasChanged }) => {
          const handleSelectBrand = createSingleAutocompleteSelectHandler(
            change,
            setSellerDisplayName,
            sellerChoices
          );

          const handleSelectCluster = createSingleAutocompleteSelectHandler(
            change,
            setClusterDisplayName,
            clusterChoices
          );

          const breadcrumbDirectory = [
            {
              name: "Sellers",
              url: "/seller"
            },
            {
              name: data.name
            }
          ];

          return (
            <Container>
              <PageBreadcrumbNavigation items={breadcrumbDirectory} />
              <div
                style={{
                  display: "flex",
                  flexDirection: "row",
                  marginLeft: "3px",
                  marginTop: "10px"
                }}
              >
                <div style={{ marginTop: "2px" }}>
                  <PageBackNavigation onBack={onBack} />
                </div>

                <div>
                  <PageHeader title={data.name} />
                </div>

                <div
                  style={{
                    display: "flex",
                    flexDirection: "row",
                    marginLeft: "auto"
                  }}
                >
                  <PageHeader>
                    <Button onClick={onView} id="viewStoreBtn">
                      <FormattedMessage defaultMessage="View Store" />
                    </Button>

                    {sellerStatus === "RVW" ? (
                      <div className={classes.buttonlabel}>
                        <Button
                          color="primary"
                          variant="contained"
                          id="activateBtn"
                          onClick={stopPropagation(() => onAssign("ACT"))}
                        >
                          <FormattedMessage defaultMessage="Activate" />
                        </Button>
                        <Button
                          className={classes.redButton}
                          variant="contained"
                          id="declineBtn"
                          onClick={stopPropagation(() => onAssign("DCL"))}
                        >
                          <FormattedMessage defaultMessage="Decline" />
                        </Button>
                      </div>
                    ) : sellerStatus === "ACT" ? (
                      <div className={classes.buttonlabel}>
                        <Button
                          className={classes.redButton}
                          variant="contained"
                          id="deactivateBtn"
                          onClick={stopPropagation(() => onAssign("INA"))}
                        >
                          <FormattedMessage defaultMessage="Deactivate" />
                        </Button>
                      </div>
                    ) : sellerStatus === "INA" ? (
                      <div>
                        <Button
                          color="primary"
                          variant="contained"
                          id="activateBtn"
                          onClick={stopPropagation(() => onAssign("ACT"))}
                        >
                          <FormattedMessage defaultMessage="Activate" />
                        </Button>
                      </div>
                    ) : (
                      <></>
                    )}
                  </PageHeader>
                </div>
              </div>
              <Grid>
                <SellerInfo
                  hasShopifyPermission={hasShopifyPermission}
                  handleShopifySync={handleShopifySync}
                  isShopifySyncLoading={isShopifySyncLoading}
                  handleVerifyShopifyConnection={handleVerifyShopifyConnection}
                  isShopifyVerifyCredentialsLoading={
                    isShopifyVerifyCredentialsLoading
                  }
                  data={data}
                  disabled={disabled}
                  errors={errors}
                  clientError={clientError}
                  updateStatusError={updateStatusError}
                  onChange={change}
                  sellerDisplayValue={sellerDisplayName}
                  clusterDisplayValue={clusterDisplayName}
                  seller={sellerChoices}
                  clusters={clusterChoices}
                  variant="update"
                  sellerData={seller}
                  fetchBrands={query => fetchBrands(query)}
                  fetchMoreBrands={fetchMoreBrands}
                  fetchClusters={query => fetchClusters(query)}
                  fetchMoreClusters={fetchMoreClusters}
                  onClusterChange={handleSelectCluster}
                  onSellerChange={handleSelectBrand}
                  onImageDelete={onImageDelete}
                  onImageUpload={onImageUpload}
                  onGenerateDeeplink={onGenerateDeeplink}
                  onFileSizeError={onFileSizeError}
                  onTagAdd={onTagAdd}
                  onTagRemove={onTagRemove}
                />
              </Grid>

              <div>
                <SaveButtonBar
                  state={saveButtonBarState}
                  onCancel={onBack}
                  onSave={submit}
                  disabled={disabled || !hasChanged}
                />
              </div>
            </Container>
          );
        }}
      </Form>
    </>
  );
};

export default SellerDetails;
